import { withNamespaces } from "react-i18next"
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import EntityRoutingService from "src/modules/data-protection/apis/EntityRoutingService";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Button, Col, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import { EMPTY_LIST } from "src/common/constants";
import RoutingModal from "./RoutingModal";
import DeleteRoutingModal from "./DeleteRoutingModal";

const Entities = ({
    t
}) => {
    const [ filters, setFilters ] = useState({
        pageIndex: 1,
        pageSize: 25
    });

    const [ showModal, setShowModal ] = useState(false);
    const [ entityToReset, setEntityToReset ] = useState(null);
    const [ entityToEdit, setEntityToEdit ] = useState(null);

    const entityService = EntityRoutingService.getInstance();

    const entitiesQuery = useQuery({
        queryKey: [
            "data-protection-fetch-settings-routed-entities-list",
            filters
        ],
        queryFn: async () => {
            return await entityService.fetchRoutedEntitiesList(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching entities."), {
                type: "error"
            });
        },
    });

    const handleTableChange = useCallback((type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch(type){
            case 'pagination':
                    setFilters((currentState) => {
                        return {
                            ...currentState,
                            pageIndex   :   page,
                            pageSize    :   sizePerPage
                        }
                    });
                break;
            case 'sort':
                    setFilters((currentState) => {
                        return {
                            ...currentState,
                            sortField   :   sortField,
                            sortOrder   :   sortOrder.toUpperCase()
                        }
                    });
                break;
            default:
                return false;
        }
    }, []);

    const columns = useMemo(() => {
        return [
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                key: 1,
                formatter: (cellContent, { legal_entity }) => {
                    return legal_entity.id
                }
            },
            {
                dataField: "legal_entity",
                text: t("Full Name"),
                sort: false,
                key: 2,
                formatter: (cellContent) => {
                    return cellContent?.title
                }
            },
            {
                dataField: "default_software_owner",
                text: t("Softwares"),
                sort: false,
                key: 3,
                formatter: (_cellContent, {
                    id,
                    default_software_owner,
                    default_software_risk_owner,
                    default_software_task_owner
                }) => {
                    return (
                        <div>
                            <span className="d-inline-flex align-items-center" id={`status-software-asset-${id}`}>
                                {default_software_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Asset')}),
                                <UncontrolledTooltip
                                    target={`status-software-asset-${id}`}
                                >
                                    {default_software_owner?.id ? default_software_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>

                            <span className="d-inline-flex align-items-center" id={`status-software-risk-${id}`}>
                                {default_software_risk_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Risk')}),
                                <UncontrolledTooltip
                                    target={`status-software-risk-${id}`}
                                >
                                    {default_software_risk_owner?.id ? default_software_risk_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>

                            <span className="d-inline-flex align-items-center" id={`status-software-task-${id}`}>
                                {default_software_task_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Task')})
                                <UncontrolledTooltip
                                    target={`status-software-task-${id}`}
                                >
                                    {default_software_task_owner?.id ? default_software_task_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>
                        </div>
                    )
                }
            },
            {
                dataField: "default_hardware_owner",
                text: t("Hardwares"),
                sort: false,
                key: 4,
                formatter: (_cellContent, {
                    id,
                    default_hardware_owner,
                    default_hardware_risk_owner,
                    default_hardware_task_owner
                }) => {
                    return (
                        <div>
                            <span className="d-inline-flex align-items-center" id={`status-hardware-asset-${id}`}>
                                {default_hardware_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Asset')}),
                                <UncontrolledTooltip
                                    target={`status-hardware-asset-${id}`}
                                >
                                    {default_hardware_owner?.id ? default_hardware_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>

                            <span className="d-inline-flex align-items-center" id={`status-hardware-risk-${id}`}>
                                {default_hardware_risk_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Risk')}),
                                <UncontrolledTooltip
                                    target={`status-hardware-risk-${id}`}
                                >
                                    {default_hardware_risk_owner?.id ? default_hardware_risk_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>

                            <span className="d-inline-flex align-items-center" id={`status-hardware-task-${id}`}>
                                {default_hardware_task_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Task')})
                                <UncontrolledTooltip
                                    target={`status-hardware-task-${id}`}
                                >
                                    {default_hardware_task_owner?.id ? default_hardware_task_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>
                        </div>
                    )
                }
            },
            {
                dataField: "default_incident_owner",
                text: t("Incident Reports"),
                sort: false,
                key: 5,
                formatter: (_cellContent, {
                    id,
                    default_incident_owner,
                    default_incident_risk_owner,
                    default_incident_task_owner
                }) => {
                    return (
                        <div>
                            <span className="d-inline-flex align-items-center" id={`status-incident-asset-${id}`}>
                                {default_incident_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Asset')}),
                                <UncontrolledTooltip
                                    target={`status-incident-asset-${id}`}
                                >
                                    {default_incident_owner?.id ? default_incident_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>

                            <span className="d-inline-flex align-items-center" id={`status-incident-risk-${id}`}>
                                {default_incident_risk_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Risk')}),
                                <UncontrolledTooltip
                                    target={`status-incident-risk-${id}`}
                                >
                                    {default_incident_risk_owner?.id ? default_incident_risk_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>

                            <span className="d-inline-flex align-items-center" id={`status-incident-task-${id}`}>
                                {default_incident_task_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Task')})
                                <UncontrolledTooltip
                                    target={`status-incident-task-${id}`}
                                >
                                    {default_incident_task_owner?.id ? default_incident_task_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>
                        </div>
                    )
                }
            },
            {
                dataField: "default_dsr_owner",
                text: t("Data Subject Requests"),
                sort: false,
                key: 6,
                formatter: (_cellContent, {
                    id,
                    default_dsr_owner,
                    default_dsr_task_owner
                }) => {
                    return (
                        <div>
                            <span className="d-inline-flex align-items-center" id={`status-dsr-asset-${id}`}>
                                {default_dsr_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Asset')}),
                                <UncontrolledTooltip
                                    target={`status-dsr-asset-${id}`}
                                >
                                    {default_dsr_owner?.id ? default_dsr_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>

                            <span className="d-inline-flex align-items-center" id={`status-dsr-task-${id}`}>
                                {default_dsr_task_owner?.id ? (
                                    <i className="ri-check-line font-size-24" />
                                ) : (
                                    <i className="ri-close-line font-size-24 text-danger" />
                                )}
                                ({t('Task')})
                                <UncontrolledTooltip
                                    target={`status-dsr-task-${id}`}
                                >
                                    {default_dsr_task_owner?.id ? default_dsr_task_owner.full_name : t('No assignee')}
                                </UncontrolledTooltip>
                            </span>
                        </div>
                    )
                }
            },
            {
                dataField: "actions",
                text: t("Action"),
                sort: false,
                key: 7,
                formatter: (cellContent, row) => {
                    return (
                        <div className="actions">
                            <button
                                className="btn btn-small text-primary"
                                onClick={() => {
                                    setEntityToEdit(row)
                                }}
                            >
                                <i className="ri-edit-line" />
                            </button>

                            <button
                                className="btn btn-small text-danger"
                                onClick={() => {
                                    setEntityToReset(row)
                                }}
                            >
                                <i className="ri-delete-bin-line" />
                            </button>
                        </div>
                    )
                }
            }
        ]
    }, [ t ]);

    const NoDataIndication = () => (
            (entitiesQuery.isFetched && !entitiesQuery.data?.itemsCount?.length) ? <div className="alert m-0" role="alert">
                <p className="text-center mb-0">
                    {t(EMPTY_LIST)}
                </p>
            </div> : <></>
        );

    return (
        <>
            <RoutingModal
                show={showModal || !!entityToEdit}
                toggle={() => {
                    setShowModal(false)
                    setEntityToEdit(null)
                }}
                prevValues={entityToEdit}
                onComplete={() => {
                    entitiesQuery.refetch();
                }}
            />
            <DeleteRoutingModal 
                show={!!entityToReset}
                toggle={() => setEntityToReset(null)}
                routingSettings={entityToReset}
                onComplete={() => {
                    entitiesQuery.refetch();
                }}
            />
            
            <PaginationProvider
                pagination={
                    paginationFactory({
                        custom: true,
                        page: filters.pageIndex,
                        sizePerPage: filters.pageSize,
                        totalSize: entitiesQuery?.data?.itemsCount || 0,
                        withFirstAndLast: true,
                        alwaysShowAllBtns: true,
                        prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                        nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                        prePageTitle: t('Pre page'),
                        firstPageTitle: t('First page'),
                        lastPageTitle: t('Last page'),
                        showTotal: false,
                        paginationTotalRenderer: (from, to, size) => {
                            return (
                                <span className="react-bootstrap-table-pagination-total">
                                    {`${t('Total Items')} ${size}`}
                                </span>
                            )
                        },
                        hideSizePerPage: false,
                        sizePerPageList: [
                            {
                                text: '25',
                                value: 25
                            },
                            {
                                text: '50',
                                value: 50
                            }
                        ]
                    })
                }>
                {
                    ({
                        paginationProps,
                        paginationTableProps
                    }) => (
                        <>
                            <Row className="mb-3">
                                <Col>
                                    <div className="d-flex justify-content-end align-items-center">
                                        <Button
                                            onClick={() => setShowModal(true)}
                                            color="primary"
                                        >
                                            <i className="ri-add-line me-1" />
                                            {t('Add Routing')}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="12">
                                    <BootstrapTable
                                        remote={{
                                            pagination: true,
                                            filter: false,
                                            sort: true,
                                            cellEdit: false,
                                            search: false
                                        }}
                                        loading={ entitiesQuery.isFetching }
                                        overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                        onTableChange={ handleTableChange }
                                        defaultSorted={[]}
                                        keyField={"secondaryId"}
                                        responsive={true}
                                        bordered={false}
                                        data={entitiesQuery.data?.routingList || []}
                                        striped={true}
                                        columns={ columns }
                                        wrapperClasses="table-responsive"
                                        classes={"table tpdd-table"}
                                        headerWrapperClasses={"thead-light"}
                                        noDataIndication={() => <NoDataIndication />}
                                        {...paginationTableProps}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12">
                                    <div className="tpdd-pagination-style-1">
                                        <PaginationTotalStandalone
                                            {...paginationProps}
                                        />

                                        <div>
                                            <PaginationListStandalone
                                                {...paginationProps}
                                            />

                                            <SizePerPageDropdownStandalone
                                                {...paginationProps}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )
                }

            </PaginationProvider>
        </>
    )
}

export default withNamespaces()(
    Entities
)